import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PrayerCard } from "@/components/PrayerCard";
import { LatestPrayersRotator } from "@/components/LatestPrayersRotator";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { HandHeart, Plus } from "lucide-react";

const PAGE_SIZE = 24;

export default function MuroOracion() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prayers, setPrayers] = useState([]);
  const [rotatorRefresh, setRotatorRefresh] = useState(0);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState("recent");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [texto, setTexto] = useState("");
  const [sending, setSending] = useState(false);
  const [page, setPage] = useState(0);

  const fetchPrayers = useCallback(() => {
    setLoading(true);
    api
      .get(`/prayers?sort=${sort}&skip=${page * PAGE_SIZE}&limit=${PAGE_SIZE}`)
      .then((res) => {
        setPrayers(res.data.prayers || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sort, page]);

  useEffect(() => {
    fetchPrayers();
  }, [fetchPrayers]);

  const syncPrayed = useCallback((prayerId, prayCount) => {
    setPrayers((prev) =>
      prev.map((p) =>
        p.id === prayerId ? { ...p, pray_count: prayCount, already_prayed: true } : p
      )
    );
  }, []);

  const handlePray = async (prayer) => {
    if (!user) {
      toast.info("Inicia sesión para orar por esta petición");
      navigate("/login");
      return;
    }
    if (prayer.already_prayed) return;
    try {
      const res = await api.post(`/prayers/${prayer.id}/pray`);
      syncPrayed(prayer.id, res.data.pray_count);
      toast.success("Gracias. Estamos contigo en oración.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo registrar tu oración. Intenta de nuevo.");
    }
  };

  const handleDelete = async (prayer) => {
    try {
      await api.delete(`/prayers/${prayer.id}`);
      setPrayers((prev) => prev.filter((p) => p.id !== prayer.id));
      setTotal((t) => Math.max(0, t - 1));
      setRotatorRefresh((r) => r + 1);
      toast.success("Petición eliminada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar la petición");
    }
  };

  const submitPrayer = async (e) => {
    e.preventDefault();
    if (texto.trim().length < 5) {
      toast.error("Escribe al menos unas palabras sobre tu petición");
      return;
    }
    setSending(true);
    try {
      const res = await api.post("/prayers", { texto: texto.trim() });
      setPrayers((prev) => [res.data, ...prev]);
      setRotatorRefresh((r) => r + 1);
      setTotal((t) => t + 1);
      setTexto("");
      setDialogOpen(false);
      toast.success("Tu petición fue publicada. La comunidad orará contigo.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo publicar la petición");
    } finally {
      setSending(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      {/* Header con imagen IA */}
      <section className="relative overflow-hidden">
        <img
          src="/img/oracion-header.jpg"
          alt="Comunidad orando unida"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#033E4F]/80 via-[#056A84]/70 to-[#033E4F]/85" />
        <div className="relative mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2">
              <HandHeart className="h-7 w-7 text-[#00CCFF]" />
              <h1 className="font-serif text-3xl font-semibold text-white sm:text-4xl">Muro de Oración</h1>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-white/85 sm:text-base">
              Comparte tu petición y permite que hermanos y hermanas oren contigo. Si una petición toca tu
              corazón, presiona “Estoy orando por ti”. Nadie debería atravesar esto solo.
            </p>
            <div className="mt-6">
              {user ? (
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="lg" className="bg-[#00CCFF] text-[#033E4F] hover:bg-[#00CCFF]/85" data-testid="prayer-wall-new-button">
                      <Plus className="mr-2 h-4 w-4" /> Publicar petición
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="font-serif text-xl">Tu petición de oración</DialogTitle>
                      <DialogDescription>
                        Comparte lo que llevas en el corazón. La comunidad orará contigo.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitPrayer} className="flex flex-col gap-4">
                      <Textarea
                        value={texto}
                        onChange={(e) => setTexto(e.target.value)}
                        placeholder="Ej: Pido oración por mi familia tras el terremoto. Perdimos nuestra casa y necesitamos fortaleza…"
                        className="min-h-[120px]"
                        maxLength={2000}
                        data-testid="prayer-form-textarea"
                      />
                      <Button type="submit" disabled={sending || texto.trim().length < 5} data-testid="prayer-form-submit">
                        {sending ? "Publicando…" : "Publicar petición"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              ) : (
                <div className="flex flex-wrap gap-3">
                  <Button size="lg" className="bg-[#00CCFF] text-[#033E4F] hover:bg-[#00CCFF]/85" onClick={() => navigate("/registro")} data-testid="prayer-wall-register-button">
                    Crear cuenta para publicar
                  </Button>
                  <Button size="lg" variant="outline" className="border-white/40 bg-white/10 text-white hover:bg-white/20" onClick={() => navigate("/login")} data-testid="prayer-wall-login-button">
                    Iniciar sesión
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Carrusel rotativo: ultimas 5 peticiones */}
      <section className="gradient-breeze border-b border-border">
        <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6">
          <LatestPrayersRotator refreshToken={rotatorRefresh} onPrayedSync={syncPrayed} />
        </div>
      </section>

      {/* Lista de peticiones (compacta, 2 columnas) */}
      <section className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6">
        <div className="mb-5 flex items-center justify-between">
          <p className="text-sm text-muted-foreground" data-testid="prayer-wall-total">
            {total === 1 ? "1 petición" : `${total} peticiones`}
          </p>
          <Select value={sort} onValueChange={(v) => { setSort(v); setPage(0); }}>
            <SelectTrigger className="h-9 w-[175px] bg-card text-sm" data-testid="prayer-wall-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Más recientes</SelectItem>
              <SelectItem value="most_prayed">Más acompañadas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-32 w-full rounded-[var(--radius)]" />
            ))}
          </div>
        ) : prayers.length === 0 ? (
          <div className="rounded-[var(--radius)] border border-border bg-card p-10 text-center" data-testid="prayer-wall-empty-state">
            <p className="font-serif text-xl font-semibold">Aún no hay peticiones</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Sé el primero/a en compartir una petición. La comunidad está aquí para acompañarte.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="prayer-wall-grid">
            {prayers.map((p) => (
              <PrayerCard key={p.id} prayer={p} onPray={handlePray} />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-3">
            <Button variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="prayer-wall-prev-page">
              Anterior
            </Button>
            <span className="text-sm text-muted-foreground">Página {page + 1} de {totalPages}</span>
            <Button variant="outline" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="prayer-wall-next-page">
              Siguiente
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
